'use client';

import { Check, ChevronLeft, ChevronRight, RotateCcw, X } from 'lucide-react';
import Link from 'next/link';
import { ReactNode, useState } from 'react';

type OptionId = 'A' | 'B' | 'C' | 'D';

type QuestionOption = {
  id: OptionId;
  label: ReactNode;
};

type Question = {
  id: string;
  number: number;
  prompt: ReactNode;
  options: QuestionOption[];
  correctOptionId: OptionId;
  correctAnswerDisplay: ReactNode;
};

const questions: Question[] = [
  {
    id: 'q4',
    number: 4,
    prompt: (
      <>
        Terjemahan yang benar untuk kalimat{' '}
        <span dir="rtl" className="whitespace-nowrap font-normal">
          {"'"}هَذَا مَسْجِدٌ{"'"}
        </span>{' '}
        adalah...
      </>
    ),
    options: [
      { id: 'A', label: 'Apakah ini masjid?' },
      { id: 'B', label: 'Ini adalah sebuah rumah' },
      { id: 'C', label: 'Ini adalah sebuah masjid' },
      { id: 'D', label: 'Bukan, ini sebuah masjid' },
    ],
    correctOptionId: 'C',
    correctAnswerDisplay: 'Ini adalah sebuah masjid',
  },
  {
    id: 'q5',
    number: 5,
    prompt:
      "Bagaimana cara bertanya 'Siapa ini?' dalam bahasa Arab, untuk menanyakan tentang seseorang?",
    options: [
      {
        id: 'A',
        label: (
          <span dir="rtl" className="block text-right">
            مَا هَذَا ؟
          </span>
        ),
      },
      {
        id: 'B',
        label: (
          <span dir="rtl" className="block text-right">
            أ هَذَا ؟
          </span>
        ),
      },
      {
        id: 'C',
        label: (
          <span dir="rtl" className="block text-right">
            مَنْ هَذَا ؟
          </span>
        ),
      },
      {
        id: 'D',
        label: (
          <span dir="rtl" className="block text-right">
            هَذَا رَجُلٌ ؟
          </span>
        ),
      },
    ],
    correctOptionId: 'C',
    correctAnswerDisplay: (
      <span dir="rtl" className="whitespace-nowrap">
        مَنْ هَذَا ؟
      </span>
    ),
  },
];

function QuestionCard({ question }: { question: Question }) {
  const [selected, setSelected] = useState<OptionId | null>(null);
  const answered = selected !== null;
  const isCorrect = selected === question.correctOptionId;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:rounded-2xl sm:p-4">
      <div className="flex items-start gap-2 sm:gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet-100 text-[11px] font-bold text-violet-700 sm:h-7 sm:w-7 sm:text-xs">
          {question.number}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400 sm:text-[10px]">
            Pilihan Ganda
          </p>
          <p className="mt-1 text-xs font-semibold leading-snug text-gray-900 sm:text-sm">
            {question.prompt}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {question.options.map((option) => {
          const isSelected = selected === option.id;
          const isCorrectOption = option.id === question.correctOptionId;
          const showAsCorrect = answered && isCorrectOption;
          const showAsWrong = answered && isSelected && !isCorrect;

          let buttonClass =
            'group flex w-full items-center gap-2 rounded-lg border bg-gray-50/60 px-2 py-1.5 text-left text-[11px] text-gray-500 transition sm:gap-3 sm:rounded-xl sm:px-3 sm:py-2 sm:text-xs';
          let badgeClass =
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[10px] font-bold text-gray-500 transition sm:h-7 sm:w-7 sm:text-xs';

          if (showAsCorrect) {
            buttonClass += ' border-violet-300 bg-violet-50';
            badgeClass = badgeClass.replace('bg-gray-100 text-gray-500', 'bg-violet-500 text-white');
          } else if (showAsWrong) {
            buttonClass += ' border-red-300 bg-red-50';
            badgeClass = badgeClass.replace('bg-gray-100 text-gray-500', 'bg-red-500 text-white');
          } else if (answered) {
            buttonClass += ' border-gray-200 opacity-60';
          } else {
            buttonClass += ' border-gray-200 hover:border-violet-200 hover:bg-violet-50/60';
          }

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                if (!answered) {
                  setSelected(option.id);
                }
              }}
              disabled={answered}
              aria-pressed={isSelected}
              className={buttonClass}
            >
              <span className={badgeClass}>{option.id}</span>
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {showAsCorrect ? (
                <Check className="h-4 w-4 shrink-0 text-violet-600" aria-hidden />
              ) : showAsWrong ? (
                <X className="h-4 w-4 shrink-0 text-red-500" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>

      {answered ? (
        <div className="mt-3 space-y-2">
          {isCorrect ? (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-[11px] font-semibold text-green-700 sm:text-xs">
              <Check className="h-4 w-4 shrink-0" aria-hidden />
              Benar — lanjutkan!
            </div>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700 sm:text-xs">
              <p className="flex items-center gap-2 font-semibold">
                <X className="h-4 w-4 shrink-0" aria-hidden />
                Belum tepat
              </p>
              <p className="mt-1 font-semibold text-red-700/90">
                Jawaban yang benar:{' '}
                <span className="font-bold">{question.correctAnswerDisplay}</span>
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 transition hover:text-violet-700 sm:text-[11px]"
          >
            <RotateCcw className="h-3 w-3" aria-hidden />
            Coba lagi
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function QuestionBankPreview() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-violet-100 bg-white p-3 shadow-xl ring-1 ring-violet-100/50 sm:rounded-3xl sm:p-5 lg:p-6"
      aria-label="Cuplikan interaktif bank soal pilihan ganda"
    >
      <div className="space-y-3 sm:space-y-4">
        {questions.map((question) => (
          <QuestionCard key={question.id} question={question} />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 sm:mt-5 sm:pt-4">
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-500 shadow-sm transition hover:bg-gray-50 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
          Prev
        </Link>
        <span className="text-[10px] text-gray-500 sm:text-[11px]">Halaman 1 dari 4</span>
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg bg-violet-500 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition hover:opacity-95 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          Next
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}
